import React from "react";
import {
  WSEmptyState,
  WSInfiniteScroll,
  WSPage
} from "@wingspanhq/fe-component-library";
import { PayerStatus } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayerRows } from "@query";
import { PageHeader } from "../../../shared/components/PageHeader";
import { PAYERS_PATH } from "../paths";
import { TablePayers } from "../components/TablePayers/TablePayers";
import { useBrowserPageTitle } from "../../../shared/hooks/useBrowserPageTitle";
import { useHistory } from "react-router-dom";

export interface RouteArchivedClientsProps {}

export const RouteArchivedClients: React.FC<RouteArchivedClientsProps> = () => {
  useBrowserPageTitle("Archived clients");
  const history = useHistory();

  // v3 archived clients = deactivated payers, off the denormalized search rows
  // (searchPayerRows) with cursor pagination. Reuses the same TablePayers as the
  // active Clients list; rows carry v3 ids, so navigation resolves on the v3 detail.
  // `isArchivedOnly` gives each row the "Restore client" action (v3 `restorePayer`,
  // WIN-15189), as the v1 table did; it's also on the client detail screen.
  const queryPayers = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    filter: { status: { eq: PayerStatus.DEACTIVATED } }
  });
  const payers = queryPayers.flatData ?? [];

  return (
    <WSPage>
      <PageHeader
        mb="L"
        title="Clients"
        rootPath={PAYERS_PATH}
        breadcrumbs={[
          {
            label: "Clients",
            onClick: () => {
              history.push(PAYERS_PATH);
            }
          },
          {
            label: "Archived clients"
          }
        ]}
      />
      {/* v1 wrapped this in WSQueries, so the first paint was a loader and a
          failed read read as an error rather than as an empty list; same
          branches here, loading first. */}
      {queryPayers.isLoading ? (
        <TablePayers payers={[]} loading isArchivedOnly />
      ) : queryPayers.isError ? (
        <WSEmptyState
          hasBorder
          bgColor="gray"
          type="error"
          title="Couldn’t load archived clients"
          description="Something went wrong while loading archived clients."
          actions={{
            buttons: [
              {
                label: "Try again",
                kind: "Secondary",
                onClick: () => queryPayers.refetch()
              }
            ]
          }}
        />
      ) : payers.length === 0 ? (
        <WSEmptyState
          hasBorder
          bgColor="gray"
          type="people"
          title="No archived clients found"
          description="Once a client is archived, they will appear here."
        />
      ) : (
        <WSInfiniteScroll
          onLoad={() => queryPayers.fetchNextPage()}
          loadMore={payers.length > 0}
          endOfList={!queryPayers.hasNextPage}
          loading={!!queryPayers.isFetchingNextPage}
        >
          <TablePayers payers={payers} isArchivedOnly />
        </WSInfiniteScroll>
      )}
    </WSPage>
  );
};
